import { useApiQuery } from '../../api/useApi';

/**
 * Les commandes TriggerFlow — #40, #87.
 *
 * ## Hook maison, comme `useConversations`
 *
 * Les hooks du web vivent dans `pages/products/api/`, que la frontière du socle
 * interdit d'atteindre (#23). Mais ce n'est pas qu'une contrainte subie : la
 * liste du web porte recherche, bornes de dates, filtre de source, tri et
 * pagination — cinq réglages que #87 écarte tous, mesures à l'appui.
 *
 * ## Ce que la mesure a écarté
 *
 * - **La source** : `pms` et `manual` sont à **zéro ligne** en base.
 * - **Le tri** : sur 453 transitions de statut, **4 ont un auteur humain**, par
 *   une seule personne. L'écran répond à « qu'est-ce qui a été commandé », pas à
 *   « que dois-je faire » — il n'y a rien à réordonner.
 * - **La pagination** : une liste de consultation, pas d'exploration.
 *
 * ## Le tri reste celui du web
 *
 * `created_at`, comme le web. #87 épingle que `date_arrival` est peut-être le vrai
 * axe de suivi — mais le dit explicitement **non mesuré**, et #17/#19 posent que
 * le mobile applique les règles existantes plutôt que d'en inventer. Dette nommée,
 * pas tranchée en passant.
 */

/** Validé — le seul statut sur lequel le web filtre déjà, sans le dire. */
const STATUT_A_TRAITER = 2;

export type OngletCommandes = 'a_traiter' | 'toutes';

export interface CommandeItem {
    id: number;
    label?: string | null;
    quantity?: number | null;
}

export interface Commande {
    id: number;
    order_status_id: number;
    customer_id: number | null;
    customer_name: string | null;
    catalog_label: string | null;
    total_price: number | null;
    created_at: string;
    date_arrival: string | null;
    guest_room: string | null;
    order_items: CommandeItem[];
}

export interface PageCommandes {
    data: Commande[];
    meta?: { total: number };
}

/**
 * Construit l'URL. Exportée pour que le test l'affirme directement — c'est elle
 * qui porte « défaut À traiter » et « tri comme le web ».
 */
export function urlCommandes(onglet: OngletCommandes): string {
    const params = new URLSearchParams({
        // 5 lignes à l'écran ; on en demande un peu plus pour savoir combien
        // manquent sans payer une seconde requête.
        per_page: '20',
        sortField: 'created_at',
        sortDirection: 'desc',
        status: onglet === 'a_traiter' ? String(STATUT_A_TRAITER) : 'all',
    });

    return `/orders?${params.toString()}`;
}

export function useCommandes(onglet: OngletCommandes) {
    return useApiQuery<PageCommandes>(urlCommandes(onglet), {}, {
        refetchOnWindowFocus: false,
    });
}
